import React, { useState, useEffect } from "react";
import { Route, useHistory } from "react-router";
import { useTranslation } from "react-i18next";
import { makeStyles } from "@material-ui/core/styles";
import { Button } from "@material-ui/core";

import { linkToUploadPhotoDialog } from "routes/photo/routes/categorise/links";
import { linkToNewPhoto } from "routes/photo/routes/new/links";
import { linkToGeotag } from "routes/photo/routes/geotag/links";

import { useGPSLocation } from "providers/LocationProvider";

import UploadPhotoDialog from "pages/photo/components/UploadPhotoDialog";
import {
  usePhotoPageState,
  usePhotoPageDispatch,
  setMetaData
} from "pages/photo/state";
import {
  isBrowserImageState,
  isImageMetaState,
  isCordovaImageState
} from "pages/photo/state/types";

import useEffectOnMount from "hooks/useEffectOnMount";

import { Item } from "../../types";
import AddNewItem from "../../components/AddNewItem/AddNewItem";
import ItemOverviewList from "../../components/ItemOverviewList/ItemOverviewList";

import loadPhoto from "./utils";
import { Capacitor } from "@capacitor/core";

import BarcodeScanner, {
  isProductInfo
} from "pages/photo/components/BarcodeScanner";

import config from "custom/config";

const FROM_BARCODE_ID = "from-barcode-scanner";

const useStyles = makeStyles((theme) => ({
  wrapper: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    padding: theme.spacing(1),
    boxSizing: "border-box"
  },
  img: {
    minHeight: "50%",
    height: "50%",
    width: "100%",
    alignSelf: "center",
    marginBottom: theme.spacing(1)
  },
  prompt: { textAlign: "center" },
  link: {
    color: theme.palette.secondary.main
  },
  or: {
    opacity: "70%",
    textAlign: "center",
    margin: 0
  },
  scanButton: {
    alignSelf: "center"
  }
}));

// NOTE: this /photo/categorise page is to be replaced with /photo/report due to the new report flow
// Keeping it here for now until new flow is fully implemented and tested.

export default function CategoriseLitterPage() {
  const state = usePhotoPageState();
  const dispatch = usePhotoPageDispatch();

  const history = useHistory();
  const gpsLocation = useGPSLocation();

  useEffectOnMount(() => {
    if (isImageMetaState(state)) {
      // do nothing - this should only be set when coming back from the geotag page
    } else if (isCordovaImageState(state)) {
      const { file, fromCamera } = state;
      loadPhoto({
        fileOrFileName: Capacitor.convertFileSrc(file.filename),
        fromCamera,
        gpsLocation,
        cordovaMetadata: file.json_metadata
          ? JSON.parse(file.json_metadata)
          : null,
        callback: (metadata) => dispatch(setMetaData(metadata))
      });
    } else if (isBrowserImageState(state)) {
      const { file, fromCamera } = state;
      loadPhoto({
        fileOrFileName: file,
        fromCamera,
        gpsLocation,
        callback: (metadata) => dispatch(setMetaData(metadata))
      });
    } else {
      history.push(linkToNewPhoto());
    }
  });

  return <CategoriseLitterPageWithFileInfo />;
}

export function CategoriseLitterPageWithFileInfo() {
  const history = useHistory();
  const state = usePhotoPageState();
  const { t } = useTranslation();

  useEffect(() => {
    if (
      isImageMetaState(state) &&
      (!state.imgLocation ||
        (state.imgLocation.latitude === config.CENTER[1] &&
          state.imgLocation.longitude === config.CENTER[0]))
    ) {
      history.replace(linkToGeotag());
    }
  }, [state, history]);

  const styles = useStyles();
  const [addingNewItem, setAddingNewItem] = useState(false);
  const [editingItem, setEditingItem] = useState<Item | null>(null);
  const [items, setItems] = useState<Item[]>([]);

  const handleRemoveItem = (index: number) => {
    const newItems = items.filter((_, itemIndex) => itemIndex !== index);

    setItems(newItems);
  };

  const addNewItem = (item: Item) => {
    const newItems = [...items, item];
    setItems(newItems);
    setAddingNewItem(false);
    setEditingItem(null);
  };

  const handleEditItemClick = (index: number) => {
    const itemToEdit = items[index];
    setEditingItem(itemToEdit);
  };

  const editItem = (item: Item) => {
    const index = editingItem && items.indexOf(editingItem);
    // if item doesn't exist returns -1, in theory shouldn't ever happen
    if (index !== null && index !== -1) {
      const newItems = items;
      newItems[index] = item;
      setItems(newItems);
      setEditingItem(null);
    } else {
      console.error("No item to edit...");
    }
  };

  const isCordova = !!window.cordova;

  return (
    <>
      <div className={styles.wrapper}>
        <img
          src={isImageMetaState(state) ? state.imgSrc : ""}
          className={styles.img}
          onClick={() => setAddingNewItem(true)}
          alt=""
        ></img>
        {addingNewItem ? (
          <AddNewItem
            onCancelClick={() => setAddingNewItem(false)}
            onConfirmClick={addNewItem}
          />
        ) : editingItem ? (
          <AddNewItem
            onCancelClick={() => setEditingItem(null)}
            onConfirmClick={editItem}
            initialItem={editingItem}
          />
        ) : (
          <>
            <p className={styles.prompt}>{t("record_litter_add_details")}</p>
            {isCordova && (
              <>
                <p className={styles.or}>{t("or_text")}</p>
                <BarcodeScanner
                  className={styles.scanButton}
                  onResult={(result) => {
                    if (isProductInfo(result)) {
                      addNewItem({
                        quantity: 1,
                        brand: { label: result.brand, id: FROM_BARCODE_ID },
                        category: {
                          id: FROM_BARCODE_ID,
                          label: result.productName
                        },
                        barcode: result.barcode
                      });
                    } else {
                      alert(result);
                    }
                  }}
                />
              </>
            )}
            <ItemOverviewList
              items={items}
              handleRemoveItem={handleRemoveItem}
              handleItemClick={handleEditItemClick}
            />
          </>
        )}
        {!(addingNewItem || editingItem) && (
          <Button
            disabled={items.length === 0}
            variant="contained"
            color="primary"
            onClick={() => history.push(linkToUploadPhotoDialog())}
            disableElevation
            fullWidth
          >
            {t("record_litter_submit_button_text")}
          </Button>
        )}
      </div>

      <Route path={linkToUploadPhotoDialog()}>
        <UploadPhotoDialog
          imgSrc={isImageMetaState(state) ? state.imgSrc : ""}
          online
          items={items}
          imgLocation={isImageMetaState(state) ? state.imgLocation : null}
        />
      </Route>
    </>
  );
}
